import { ReactNode } from 'react';
import { t } from 'i18next';
import { Area } from 'screens/clients/modules/CompanyDetails/components/AreasList/AreasList';
import { useSelectedCustomerAreas } from '@hooks';
import { parseAmount } from '@utils';
import { Yup } from '@validations';

export interface RowItem<T> {
  label: string;
  value: (data: T) => ReactNode;
  shouldHideItem?: (data: T) => boolean;
}

export interface AreaCardProps {
  area: AreaInfo;
  fetchAreas: () => void;
  fetchCompany: () => void;
  fetchMovements: () => void;
}

export type AreaInfo = Area | (Company & { isCompany: true });

export interface Company {
  id: number;
  cuil: string;
  description: string;
  balance: string;
  balanceAvailable: string;
  customerType: string;
}

/** Returns the rows to be displayed in the area card */
export const ROWS = [
  {
    label: 'COMMON:AREA',
    value: (item: AreaInfo) => 'fantasyName' in item && item.fantasyName,
    shouldHideItem: (item: AreaInfo) => 'isCompany' in item,
  },
  {
    label: 'COMMON:COMPANY',
    value: (item: AreaInfo) => 'description' in item && item.description,
    shouldHideItem: (item: AreaInfo) => !('isCompany' in item),
  },
  {
    label: 'COMMON:COST_CENTER',
    value: (item: AreaInfo) => 'costCenter' in item && item.costCenter,
    shouldHideItem: (item: AreaInfo) => 'isCompany' in item,
  },
];
/**
 * Get assign balance schema
 * @param totalBalance Company available balance.
 * @returns
 */
export const VALIDATION_ASSIGN_BALANCE_TO_AREA_SCHEMA = (totalBalance: number) => {
  return Yup.object().shape({
    areaId: Yup.string().required('COMMON:VALIDATIONS:REQUIRED_FIELD'),
    amount: Yup.string()
      .required('COMMON:VALIDATIONS:REQUIRED_FIELD')
      .transform((value) => value.replace(',', '.'))
      .matches(/^\d+(\.\d+)?$/, 'COMMON:VALIDATIONS:INVALID_AMOUNT')
      .min(1, 'COMMON:VALIDATIONS:INVALID_AMOUNT')
      .test(
        'is-number',
        'COMMON:VALIDATIONS:INVALID_AMOUNT',
        (value) => !isNaN(parseFloat(value ?? ''))
      )
      .test('checkDecimals', 'COMMON:VALIDATIONS:INVALID_AMOUNT_DECIMALS', (num) => {
        const decimals = String(num).split('.')[1];

        if (!decimals) {
          return true;
        } else {
          return decimals.length < 3;
        }
      })
      .test(
        'checkMax',
        t('CLIENTS:ASSIGN_BALANCE_MODAL:MAX_AMOUNT_ERROR', {
          totalBalance: parseAmount(totalBalance),
        }),
        (num) => {
          return Number(num) <= totalBalance;
        }
      ),
  });
};

/**
 * Get form assign balance inputs
 * @param amount Amount to show on input placeholder.
 * @returns Array of inputs.
 */
export const CONFIG_ASSIGN_BALANCE_TO_AREA_FIELD = (amount: number) => [
  {
    name: 'areaId',
    label: 'COMMON:SELECT_AREA_SELECTOR',
    defaultValue: '',
    type: 'select',
    getOptions: useSelectedCustomerAreas,
    parseOption: ({ id, fantasyName }: { id: string; fantasyName: string }) => ({
      value: id,
      text: fantasyName,
    }),
  },
  {
    name: 'amount',
    defaultValue: '',
    type: 'number',
    placeholder: parseAmount(amount),
  },
];
